/**
 * feedbackUpload — bundles a `/feedback` submission into one unit on S3.
 *
 * Layout written under the existing workflows bucket using a synthetic
 * workflowId `_feedback_<id>` so the prefix sorts away from real designs:
 *
 *   {consultantId}/_feedback_{feedbackId}/inputs/manifest.json
 *   {consultantId}/_feedback_{feedbackId}/inputs/<attachment-1>
 *   {consultantId}/_feedback_{feedbackId}/inputs/<attachment-2>
 *   ...
 *
 * Retrieval: list `_feedback_*` prefixes in the bucket, read each
 * `manifest.json` to get the user's text + context, attachments are right
 * next to it. End-of-day triage is `aws s3 ls s3://bucket/<email>/` then
 * grep for `_feedback_`.
 *
 * The whole thing reuses the existing /upload presign + base64 path —
 * NO new backend route, NO API Gateway change needed.
 */

// @ts-expect-error — api.js is JS without types.
import { uploadDocument } from './api';

const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024; // 25 MB per attachment

export interface FeedbackContext {
  /** Caller's identity — used as the S3 first-level prefix. */
  consultantId: string;
  consultantEmail: string;
  /** Customer name — dialogs require it; composer-based feedback passes
   *  the design's stored value ('' when the design has none). */
  customerName?: string;
  /** Optional pointers back to whatever the user was looking at when
   *  they hit /feedback. All of these are written to manifest.json so
   *  the triager can jump straight to the offending state. */
  designId?: string;
  workflowId?: string;
  currentStage?: string;
  pageUrl?: string;
  /** The user's typed issue description. */
  description: string;
  /** Optional category tag — "bug" | "feature" | "ux" | "other". */
  category?: 'bug' | 'feature' | 'ux' | 'other';
  /** Any extra free-form context the parent wants to bake in (e.g.
   *  last few chat messages, current pipeline state, etc.). */
  extra?: Record<string, unknown>;
}

export interface FeedbackResult {
  feedbackId: string;
  manifestS3Key: string;
  attachmentS3Keys: string[];
  manifestPath: string;
}

function newFeedbackId(): string {
  // 8-hex random tail keeps the prefix sortable + collision-free for
  // anything below ~thousands per second per user. The date-prefix
  // makes "aws s3 ls" sort chronologically without parsing JSON.
  const stamp = new Date().toISOString().replace(/[:.]/g, '').slice(0, 15);
  const rand = Math.random().toString(36).slice(2, 10);
  return `${stamp}-${rand}`;
}

/**
 * Upload every attachment + the manifest JSON as one bundle. Returns
 * the feedbackId and the manifest's S3 key on success. Throws on any
 * upload failure — partial bundles are dropped on the floor (the
 * triager script ignores prefixes without a manifest.json).
 */
export async function submitFeedback(
  attachments: File[],
  ctx: FeedbackContext,
): Promise<FeedbackResult> {
  const feedbackId = newFeedbackId();
  const syntheticWorkflowId = `_feedback_${feedbackId}`;

  // Hard-fail on oversize attachments before we send anything — partial
  // bundles are worse than no bundle.
  for (const f of attachments) {
    if (f.size > MAX_ATTACHMENT_BYTES) {
      throw new Error(
        `Attachment "${f.name}" is ${(f.size / 1024 / 1024).toFixed(1)} MB — over the 25 MB limit.`,
      );
    }
  }

  // Upload attachments in parallel via the existing /upload endpoint.
  // uploadDocument honors workflowId verbatim, so all keys land under
  // the same synthetic prefix.
  const uploadPromises = attachments.map((f) =>
    uploadDocument(
      f,
      ctx.consultantId,
      ctx.consultantEmail,
      syntheticWorkflowId,
    ).then((res: any) => ({ name: f.name, size: f.size, type: f.type, s3Key: res?.s3Key || '' })),
  );

  const uploaded = await Promise.all(uploadPromises);
  const attachmentS3Keys = uploaded.map((u) => u.s3Key).filter(Boolean);

  // Build the manifest. Everything the triager needs to reproduce the
  // bug is in here — no DDB lookup required.
  const manifest = {
    schemaVersion: 1,
    feedbackId,
    submittedAt: new Date().toISOString(),
    submittedBy: {
      consultantId: ctx.consultantId,
      consultantEmail: ctx.consultantEmail,
      customerName: ctx.customerName || '',
    },
    category: ctx.category || 'other',
    description: ctx.description,
    context: {
      designId: ctx.designId || null,
      workflowId: ctx.workflowId || null,
      currentStage: ctx.currentStage || null,
      pageUrl: ctx.pageUrl || (typeof window !== 'undefined' ? window.location.href : null),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      viewport:
        typeof window !== 'undefined'
          ? { w: window.innerWidth, h: window.innerHeight }
          : null,
      ...(ctx.extra || {}),
    },
    attachments: uploaded.map((u) => ({
      filename: u.name,
      sizeBytes: u.size,
      contentType: u.type,
      s3Key: u.s3Key,
    })),
  };

  // Write the manifest as a JSON file via the same /upload flow. It
  // accepts .json in the allowlist and the resulting key sits next to
  // the attachments in the same `_feedback_<id>` prefix.
  const manifestBlob = new Blob([JSON.stringify(manifest, null, 2)], {
    type: 'application/json',
  });
  const manifestFile = new File([manifestBlob], 'manifest.json', {
    type: 'application/json',
  });

  const manifestUpload: any = await uploadDocument(
    manifestFile,
    ctx.consultantId,
    ctx.consultantEmail,
    syntheticWorkflowId,
  );

  return {
    feedbackId,
    manifestS3Key: manifestUpload?.s3Key || '',
    attachmentS3Keys,
    manifestPath: `${ctx.consultantId}/${syntheticWorkflowId}/`,
  };
}
